import { useEffect, useRef } from "react";
import { Box, Paper, useTheme } from "@mui/material";
import { EmojiPicker as FrimousseEmojiPicker } from "frimousse";
import { useTranslation } from "react-i18next";
// CSS styles for larger emojis
const emojiPickerStyles = `
  [frimousse-emoji] {
    font-size: 2rem !important;
    width: 3rem !important;
    height: 3rem !important;
    padding: 0.5rem !important;
    border: none !important;
    border-radius: 8px !important;
    background: transparent !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease !important;
  }
  [frimousse-emoji]:hover {
    background-color: rgba(0, 0, 0, 0.08) !important;
  }
  /* Hide category titles using the correct class - override inline styles */
  .frimousse-category {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    position: absolute !important;
    top: -9999px !important;
  }
  [class*="frimousse-category"] {
    display: none !important;
    visibility: hidden !important;
  }
`;

interface EmojiPickerProps {
    onEmojiSelect: (emoji: string) => void;
    onClose?: () => void;
}

export const EmojiPicker = ({ onEmojiSelect, onClose }: EmojiPickerProps) => {
    const theme = useTheme();
    const pickerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const handleEmojiClick = (emoji: any) => {
        onEmojiSelect(emoji.unicode || emoji.emoji || emoji);
        onClose?.();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                onClose?.();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    // Add click listener for emoji selection as fallback
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target && target.hasAttribute("frimousse-emoji")) {
                const emoji = target.textContent;
                if (emoji) {
                    handleEmojiClick({ unicode: emoji, emoji: emoji });
                }
            }
        };

        if (pickerRef.current) {
            pickerRef.current.addEventListener("click", handleClick);
        }

        return () => {
            if (pickerRef.current) {
                pickerRef.current.removeEventListener("click", handleClick);
            }
        };
    }, []);

    // Inject CSS styles
    useEffect(() => {
        const styleId = "frimousse-emoji-styles";
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = styleId;
            styleElement.textContent = emojiPickerStyles;
            document.head.appendChild(styleElement);
        }

        return () => {
            // Cleanup on unmount
            const element = document.getElementById(styleId);
            if (element) {
                element.remove();
            }
        };
    }, []);

    return (
        <Paper
            ref={pickerRef}
            elevation={8}
            sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                mb: 1,
                borderRadius: 2,
                overflow: "hidden",
                zIndex: 1000,
                width: 480,
                height: 400,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <FrimousseEmojiPicker.Root onEmojiSelect={handleEmojiClick}>
                <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <FrimousseEmojiPicker.Search
                        placeholder={t("social.create_post.search_emojis", "Search emojis...")}
                        style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            outline: "none",
                            fontSize: "14px",
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary,
                        }}
                    />
                </Box>
                <FrimousseEmojiPicker.Viewport
                    style={{
                        height: "320px",
                        overflow: "auto",
                        padding: "8px",
                    }}
                >
                    <FrimousseEmojiPicker.Loading
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: theme.palette.text.secondary,
                            fontSize: "14px",
                        }}
                    >
                        {t("social.create_post.loading_emojis", "Loading emojis...")}
                    </FrimousseEmojiPicker.Loading>
                    <FrimousseEmojiPicker.Empty
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: theme.palette.text.secondary,
                            fontSize: "14px",
                        }}
                    >
                        {t("social.create_post.no_emoji_found", "No emoji found.")}
                    </FrimousseEmojiPicker.Empty>
                    <FrimousseEmojiPicker.List
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(6, 1fr)",
                            gap: "8px",
                            padding: "8px",
                        }}
                    />
                </FrimousseEmojiPicker.Viewport>
            </FrimousseEmojiPicker.Root>
        </Paper>
    );
};
